import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  getToken,
  setToken,
  type HubUser,
  type LoginResponse,
} from "@/lib/api";
import {
  authHubHasSession,
  authHubSignOutUrlForHub,
  preferAuthHubSso,
} from "@/lib/atwAuthHub";
import { resetHubSearch } from "@/lib/hubSearch";
import { claimDeviceCaches } from "@/lib/cacheOwner";
import { isHubNativeShell } from "@/lib/hubShell";

const USER_CACHE_KEY = "atw-hub-user";

type AuthState = {
  user: HubUser | null;
  ready: boolean;
  signIn: (payload: LoginResponse) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function readCachedUser(): HubUser | null {
  try {
    if (!getToken()) return null;
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HubUser;
    if (!parsed || typeof parsed.email !== "string" || !parsed.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedUser(user: HubUser | null) {
  if (user?.email) claimDeviceCaches(user.email);
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Cold start: paint with last signed-in user while /api/me refreshes.
  const [user, setUser] = useState<HubUser | null>(() => readCachedUser());
  const [ready, setReady] = useState(() => Boolean(readCachedUser()) || !getToken());

  const clearLocalSession = useCallback(() => {
    setToken(null);
    setUser(null);
    writeCachedUser(null);
    resetHubSearch();
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      writeCachedUser(null);
      setReady(true);
      return;
    }
    // Browser Hub shares the Auth cookie, so a missing cookie means sign-out.
    // The Mac/iOS app signs in through its own window, then opens Hub with only
    // the Bearer token. That web view has no Auth cookie. Do not wipe the token.
    if (preferAuthHubSso() && !isHubNativeShell()) {
      const authAlive = await authHubHasSession();
      if (!authAlive) {
        clearLocalSession();
        setReady(true);
        return;
      }
    }
    try {
      const me = await api<HubUser>("/api/me");
      setUser(me);
      writeCachedUser(me);
    } catch {
      // Keep cached user on transient failures so Mail can still open offline-ish.
      const cached = readCachedUser();
      if (!cached) {
        setUser(null);
        writeCachedUser(null);
      }
    } finally {
      setReady(true);
    }
  }, [clearLocalSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      writeCachedUser(null);
    };
    window.addEventListener("atw-hub-unauthorized", onUnauthorized);
    return () => window.removeEventListener("atw-hub-unauthorized", onUnauthorized);
  }, []);

  useEffect(() => {
    if (!preferAuthHubSso()) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && getToken()) void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      signIn(payload) {
        setToken(payload.token);
        setUser(payload.user);
        writeCachedUser(payload.user);
        setReady(true);
      },
      async signOut() {
        try {
          if (getToken()) await api("/api/auth/logout", { method: "POST" });
        } catch {
          /* ignore */
        }
        clearLocalSession();
        // Suite SSO: always clear `.atw.net` NextAuth via Auth hub (not Hub-only logout).
        if (preferAuthHubSso()) {
          window.location.assign(authHubSignOutUrlForHub("/login"));
        }
      },
      refresh,
    }),
    [user, ready, refresh, clearLocalSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth requires AuthProvider");
  return ctx;
}
